/*
 * LOCAL-ONLY: This file is a local-only addition to fluxer_app and will never exist upstream.
 * It manages synthetic history entries so the OS back gesture / hardware back button works for relocation at phone
 * width, as Android Back does in Obyrus (relocation parity, 2026-10-30):
 *  - while relocation is open (the selection bar), one entry: popping it closes relocation;
 *  - while the relocate panel is open over the bar, a second entry: popping it closes the panel, back to the bar.
 * Relocation starts from the message menu's "Relocate messages" (RelocateMenuEntry), which waits for the menu sheet's
 * own history entry to be gone first, so the entries here never collide with it.
 *
 * Lines to check on upstream merge: none — exclude this file entirely from any upstream sync.
 */

// SPDX-License-Identifier: AGPL-3.0-or-later

import SelectMode from '@app/features/channel/state/SelectMode';
import {useEffect} from 'react';

const MOBILE_SELECT_MODE_HISTORY_KEY = '__fluxer_mobile_select_mode';

type Level = 'bar' | 'panel';

interface MobileSelectModeHistoryState {
	[MOBILE_SELECT_MODE_HISTORY_KEY]: {
		channelId: string;
		level: Level;
	};
}

function getCurrentPath(): string {
	return window.location.pathname + window.location.search + window.location.hash;
}

function currentLevel(channelId: string): Level | null {
	if (typeof window === 'undefined') return null;
	const state = window.history.state as Partial<MobileSelectModeHistoryState> | null;
	const value = state?.[MOBILE_SELECT_MODE_HISTORY_KEY];
	return value?.channelId === channelId ? value.level : null;
}

function pushEntry(channelId: string, level: Level): void {
	if (typeof window === 'undefined' || currentLevel(channelId) === level) return;
	const nextState: MobileSelectModeHistoryState = {[MOBILE_SELECT_MODE_HISTORY_KEY]: {channelId, level}};
	window.history.pushState(nextState, '', getCurrentPath());
}

// LOCAL-ONLY: exclude from upstream sync.
export function useMobileSelectModeHistoryDismiss(channelId: string, isMobileLayout: boolean): void {
	const activeHere = isMobileLayout && SelectMode.isActive && SelectMode.channelId === channelId;
	const panelOpenHere = activeHere && SelectMode.isPanelOpen;

	useEffect(() => {
		if (activeHere && currentLevel(channelId) == null) pushEntry(channelId, 'bar');
	}, [activeHere, channelId]);

	useEffect(() => {
		if (panelOpenHere) pushEntry(channelId, 'panel');
	}, [panelOpenHere, channelId]);

	useEffect(() => {
		if (!isMobileLayout) return;
		const handlePopState = () => {
			if (!SelectMode.isActive || SelectMode.channelId !== channelId) return;
			const level = currentLevel(channelId);
			if (SelectMode.isPanelOpen && level !== 'panel') {
				SelectMode.closePanel();
				return;
			}
			if (!SelectMode.isPanelOpen && level == null && !SelectMode.busy) {
				SelectMode.deactivate();
			}
		};
		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [channelId, isMobileLayout]);
}

/** Tapping outside the panel: pops the panel's entry when it is on top (the popstate closes the panel). */
export function dismissRelocatePanel(channelId: string): void {
	if (currentLevel(channelId) === 'panel') {
		window.history.back();
		return;
	}
	SelectMode.closePanel();
}

/** The bar's "Close relocation mode": closes relocation, then drops the bar's own entry so Back isn't spent on it. */
export function closeRelocationAtPhoneWidth(channelId: string): void {
	SelectMode.deactivate();
	if (currentLevel(channelId) === 'bar') {
		window.history.back();
	}
}
